import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import HelcooLogo from "../components/HelcooLogo";
import { useLanguage } from "../context/LanguageContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { t, isRTL, language } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/chat';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const response = await api.post(endpoint, formData);

      if (response.data) {
        if (response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
        }
        
        toast.success(isLogin ? t("loginSuccess") : t("registerSuccess"));
        window.location.href = "/chat";
      }
    } catch (error) {
      const message = error.response?.data?.detail || t("errorOccurred");
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      {/* Elegant Background */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-foreground/3 rounded-full blur-[120px] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-foreground/2 rounded-full blur-[100px] -translate-x-1/3 translate-y-1/3" />
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <motion.div 
          className="flex flex-col items-center mb-10"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
        >
          <div className="relative mb-4">
            <HelcooLogo size={90} animated={true} />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>
            HELCOO
          </h1>
          <p className="text-muted-foreground mt-2 text-center">
            {language === "ar" ? "منصة الذكاء الاصطناعي المتكاملة" : "The All-in-One AI Platform"}
          </p>
        </motion.div>

        {/* Card */}
        <motion.div 
          className="p-8 rounded-3xl bg-card border border-border"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {/* Tabs */}
          <div className="flex bg-secondary rounded-2xl p-1.5 mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3.5 rounded-xl font-semibold transition-all duration-300 ${
                isLogin 
                  ? "bg-foreground text-background" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="login-tab"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {t("login")}
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3.5 rounded-xl font-semibold transition-all duration-300 ${
                !isLogin 
                  ? "bg-foreground text-background" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="register-tab"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {t("register")}
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name (Register only) */}
            {!isLogin && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="relative"
              >
                <User className="absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground right-4 z-10 pointer-events-none" />
                <input
                  type="text"
                  placeholder={t("name")}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-5 py-4 pr-12 rounded-xl bg-secondary border border-border text-foreground focus:outline-none focus:border-foreground/30 focus:ring-4 focus:ring-foreground/5 transition-all duration-300 text-right"
                  required={!isLogin}
                  data-testid="name-input"
                  dir="rtl"
                />
              </motion.div>
            )}

            {/* Email */}
            <div className="relative">
              <Mail className="absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground right-4 z-10 pointer-events-none" />
              <input
                type="email"
                placeholder={t("email")}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-5 py-4 pr-12 rounded-xl bg-secondary border border-border text-foreground focus:outline-none focus:border-foreground/30 focus:ring-4 focus:ring-foreground/5 transition-all duration-300 text-left placeholder:text-right"
                required
                data-testid="email-input"
                dir="ltr"
                style={{ direction: 'ltr', textAlign: 'left' }}
              />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock className="absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground right-4 z-10 pointer-events-none" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("password")}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-5 py-4 pr-12 pl-12 rounded-xl bg-secondary border border-border text-foreground focus:outline-none focus:border-foreground/30 focus:ring-4 focus:ring-foreground/5 transition-all duration-300 text-left placeholder:text-right"
                required
                minLength={6}
                data-testid="password-input"
                dir="ltr"
                style={{ direction: 'ltr', textAlign: 'left' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 -translate-y-1/2 left-4 z-20 p-1 text-muted-foreground hover:text-foreground transition-colors rounded-lg"
                data-testid="toggle-password-btn"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full py-4 rounded-xl bg-foreground text-background font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              data-testid="submit-btn"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>{isLogin ? t("enterBtn") : t("createAccount")}</span>
                  <Sparkles className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground">{t("or")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Google Login */}
          <motion.button
            onClick={handleGoogleLogin}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="w-full flex items-center justify-center gap-3 bg-secondary hover:bg-secondary/80 font-medium py-4 px-6 rounded-xl transition-all duration-300 border border-border"
            data-testid="google-login-btn"
            style={{ fontFamily: 'Cairo, sans-serif' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{t("loginWithGoogle")}</span>
          </motion.button>
        </motion.div>

        {/* Back to Home */}
        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowRight className="w-4 h-4" />
            <span>{t("backToHome")}</span>
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
